import { isThetaError, type Upload, type UploadScope } from "@theta/api-client";
import { thetaApi, dataOf } from "./theta";

export type { Upload };

/**
 * Upload one file WITH A DECLARED INTENT — `POST /api/v1/uploads` (§1.11).
 *
 * WHAT CHANGED, AND IT IS NOT THE TRANSPORT. The old call was
 * `POST /uploads.json` with `type=composer|avatar`, which said nothing about
 * who may read the result: the row got an ACL when something finally
 * referenced it, and until then it was addressable by anyone who guessed the
 * url. v1 AUTHORISES THE SCOPE HERE, so the file has an ACL before a post,
 * a document or a group cover has ever pointed at it — which is also why
 * `scope` is required rather than defaulted. There is no sensible default for
 * "who is allowed to read this"; a caller that does not know is a caller that
 * has not decided.
 *
 * The response is `Upload`, and the ids on it are branded — `attachments:
 * [{uploadId}]` on a post now takes the value straight through with no cast,
 * which is the seam `PostComposer` used to carry.
 *
 * Rejects with an `Error` carrying the SERVER's message when it has one
 * (`fileTooLarge`, `unsupportedFileType` and friends are things a person can
 * act on), falling back to `fallbackMessage`.
 */
export async function uploadFile(
  file: File,
  {
    scope,
    fallbackMessage = "Filen kunne ikke uploades. Prøv igen.",
  }: { scope: UploadScope; fallbackMessage?: string }
): Promise<Upload> {
  const form = new FormData();
  form.append("file", file);
  form.append("scope", JSON.stringify(scope));

  try {
    return dataOf<Upload>(
      await thetaApi.http.POST("/uploads", {
        /**
         * THE CAST IS THE CONTRACT'S GAP, not a shortcut past the client.
         * `backend/openapi.json` describes this route's `multipart/form-data`
         * body in PROSE and declares no `requestBody`, so `schema.d.ts` types
         * it `never` and a `FormData` cannot be passed without one. Everything
         * else still applies — the path is checked, `X-Theta-Client` and the
         * required `Idempotency-Key` (D13) are added by the client's
         * middleware, and the cookie rides on `credentials: "include"`.
         *
         * `bodySerializer` is the identity so `openapi-fetch` does not JSON
         * the FormData and, crucially, does not set a Content-Type — the
         * browser must write its own multipart boundary.
         */
        body: form as never,
        bodySerializer: (b: unknown) => b as FormData,
      })
    );
  } catch (err) {
    if (isThetaError(err) && err.message) throw new Error(err.message);
    throw new Error(fallbackMessage);
  }
}

/**
 * A VIDEO IS NOT WAITED FOR — OSO-490 REVERSED D1
 *
 * `POST /uploads` answers `processing` for a video and `ready` for everything
 * else, and THAT ANSWER IS THE END OF THE COMPOSER'S INTEREST. There used to
 * be an `awaitUploadReady` here that polled `GET /uploads/{id}` every 2 s for
 * up to five minutes and kept "Slå op" disabled the whole time; it is gone,
 * with its two timing constants and its injectable clock.
 *
 * WHY IT WENT. `docs/port/media-pipeline.md` §3.4 argued that waiting was
 * cheaper than the alternative: a pending state in the serializer, a
 * placeholder in three components, and a `?ifChanged=` token that has to move
 * when the transcode lands. Every one of those now exists — slices 1–3 built
 * them for the row rather than for the attachment — so the cost the wait was
 * avoiding has already been paid, and all the wait still bought was a member
 * staring at a disabled button. The post goes out when the video does.
 *
 * WHAT DID NOT GO: `failed` IS STILL REFUSED AT ATTACH TIME. A transcode that
 * has already failed by the time the 201 lands has nothing to attach — the
 * server refuses it in `findUploads` too — and falling back to the original is
 * the black rectangle OSO-437 reported. The message below is what the tray
 * shows for it.
 *
 * THE FIVE-MINUTE TIMEOUT was the queue-is-down case, and the SERVER owns that
 * now: `theta.reap-stuck-uploads` marks anything `processing` for over 30 min
 * as `failed` every five minutes, which routes to the author as a
 * `media_failed` notification. A client-side deadline could only ever have
 * covered a member who kept the tab open.
 */
export const UPLOAD_PROCESSING_FAILED =
  "Videoen kunne ikke behandles og kan ikke vedhæftes.";
